import {Injectable} from '@angular/core';
import {HttpClient, HttpParams} from "@angular/common/http";
import {BehaviorSubject, Observable} from "rxjs";
import {getPageDefaults, getPageNumberAndSize, Page} from "../../../models/paginator";
import {Customer} from "../../../models/customer";
import {environment} from "../../../environments/environment";
import {getEntityPage} from "../../../services/util/util";
import {Order} from "../../../models/order";

/** One shipment as the backend returns it after creating or retrying it in Eco. */
export interface ShipmentResult {
    shipmentId: number;
    orderId: number;
    shipmentStatus: string;
    trackingUrl?: string;
    trackingNumber?: string;
    errorMessage?: string;
}

export interface BulkCreateShipmentsResponse {
    total: number;
    successful: number;
    failed: number;
    shipments: ShipmentResult[];
}

@Injectable()
export class AdminPanelService {

    private customersPageSubject$: BehaviorSubject<Page<Customer>> = new BehaviorSubject<Page<Customer>>(getPageDefaults());

    private ordersPageSubject$: BehaviorSubject<Page<Order>> = new BehaviorSubject<Page<Order>>(getPageDefaults());

    constructor(
        private readonly httpClient: HttpClient) {
    }

    get customersPage$(): Observable<Page<Customer>> {
        return this.customersPageSubject$.asObservable();
    }

    get ordersPage$(): Observable<Page<Order>> {
        return this.ordersPageSubject$.asObservable();
    }

    getAllCustomers(pageNumber?: number, pageSize?: number): void {
        const url: string =
            environment.domain.backend +
            environment.api.customers +
            getPageNumberAndSize(pageNumber, pageSize);

        getEntityPage<Customer>(this.customersPageSubject$, this.httpClient, url);
    }

    getAllCustomersWithCouponCode(): Observable<Customer[]> {
        const url: string =
            environment.domain.backend +
            environment.api.customers +
            '/with-coupon'

        return this.httpClient.get<Customer[]>(url);
    }

    /** Newest first, each with its Eco shipment (if one was created). */
    getPaginatedOrders(pageNumber: number, pageSize: number): void {
        const url: string = `${environment.domain.backend}/admin/orders`;

        const params = new HttpParams()
            .set('page', pageNumber.toString())
            .set('size', pageSize.toString());

        this.httpClient.get<Page<Order>>(url, {params}).subscribe((page) => {
            this.ordersPageSubject$.next(page);
        });
    }

    /** Sends the selected orders to Eco Logistics in one bulk import. */
    bulkCreateShipments(orderIds: number[]): Observable<BulkCreateShipmentsResponse> {
        const url = `${environment.domain.backend}/admin/shipments/bulk-create`;
        return this.httpClient.post<BulkCreateShipmentsResponse>(url, {orderIds});
    }

    /** Sends a failed shipment to Eco again. */
    retryShipment(shipmentId: number): Observable<ShipmentResult> {
        const url = `${environment.domain.backend}/admin/shipments/${shipmentId}/retry`;
        return this.httpClient.post<ShipmentResult>(url, {});
    }
}
